"use client";

import { FeatureSelectorFeatures } from "@/components/FeatureSelector/utils";
import React, { useState } from "react";
import { Product } from "../../pages/api/products";
import { PRODUCTS } from "@/shared/product_ids";
import { Badge, Box, Grid, Label, RadioGroupItem, Stack } from "../ui";

export function FeatureSelector({
  disabled,
  onChange,
  product,
  fontFeatures,
}: {
  disabled?: boolean;
  onChange?: (features: FeatureSelectorFeatures) => void;
  product?: Product;
  fontFeatures: FeatureSelectorFeatures;
}) {
  const [featureSettings, setFeatureSettings] = useState(fontFeatures);

  return (
    <Grid columns={2} gap={3}>
      {Object.entries(featureSettings).map(([label, feature]) => {
        const { values, sample, operation } = feature;
        const isNotAvailable =
          product && [PRODUCTS.TRIAL, PRODUCTS.BASIC].includes(product.id);
        return (
          <Stack key={label} gap={1}>
            <Label
              aria-label={label}
              css={{
                display: { sm: "none", md: "flex" },
                color: "comment",
                fontSize: 0,
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                as="span"
                title={label}
                css={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {label}
              </Box>
              {!product && feature.isPremium && (
                <Badge
                  shape="outline"
                  size="small"
                  title="Available in Plus and Complete only"
                  css={{ cursor: "help" }}
                >
                  ⚡
                </Badge>
              )}
              {product && isNotAvailable && (
                <Box
                  as="abbr"
                  title="Upgrade to access the feature"
                  css={{
                    fontSize: 1,
                    color: "primary",
                    display: "inline-block",
                    textDecoration: "none",
                    cursor: "help",
                  }}
                >
                  
                </Box>
              )}
            </Label>
            <Grid
              css={{
                gridTemplateColumns: `repeat(auto-fit, minmax(3em, 1fr))`,
              }}
            >
              {values.map((option) => {
                const { name, value } = option;
                return (
                  <RadioGroupItem
                    key={`${name}-${value}`}
                    name={label}
                    value={value}
                    checked={value === feature.value}
                    disabled={isNotAvailable || disabled}
                    onChange={(value) => {
                      setFeatureSettings((currentFeatures) => {
                        const newFeatures = { ...currentFeatures };
                        newFeatures[label] = {
                          ...currentFeatures[label],
                          value,
                        };
                        onChange?.(newFeatures);
                        return newFeatures;
                      });
                    }}
                  >
                    <Box
                      css={{
                        py: 1,
                        fontSize: 4,
                        whiteSpace: "nowrap",
                        lineHeight: 1,
                        transition: "opacity .75s",
                        fontFeatureSettings: `"${value}" ${
                          operation === "freeze" ? "on" : "off"
                        }`,
                        opacity:
                          value === feature.value || feature.value === ""
                            ? 1
                            : 0.25,
                      }}
                      dangerouslySetInnerHTML={{ __html: sample ?? "" }}
                    />
                    <Box
                      css={{
                        display: { sm: "none", lg: "block" },
                        fontSize: 0,
                        color: "comment",
                      }}
                    >
                      {value === ""
                        ? "—"
                        : operation === "exclude"
                        ? `No ${value}`
                        : `${value}`}
                    </Box>
                  </RadioGroupItem>
                );
              })}
            </Grid>
          </Stack>
        );
      })}
    </Grid>
  );
}
